/**
 * Fuzzy clustering for receipt-derived store names.
 *
 * Receipt OCR/LLM extraction produces noisy variants of the same store —
 * "セブンイレブン渋谷店" / "セブン-イレブン渋谷店" / "セブンイレブン" — so simple
 * exact-match grouping under-counts repeat visits. We cluster by:
 *
 *   1. NFKC + lower-case + strip whitespace/punctuation/branch suffix
 *   2. Either high overall similarity (Levenshtein) OR a strong shared prefix
 *      (chain stores share a brand prefix, the suffix is the location)
 *
 * The chosen "canonical" name for a cluster is the most frequent variant,
 * with the shortest length winning ties (chain root tends to be shorter).
 */

// NB: do NOT include 'ー' (U+30FC katakana prolonged sound mark) — it is part
// of legitimate names like 'スターバックス' and isn't a separator.
const STRIP_RE = /[\s　\-‐－−‒–—・,，、。.()（）\[\]【】]/g
const BRANCH_SUFFIX_RE = /(店|支店|本店)$/

export function normalizeStoreName(input: string): string {
  return input
    .normalize('NFKC')
    .toLowerCase()
    .replace(STRIP_RE, '')
    .replace(BRANCH_SUFFIX_RE, '')
    .trim()
}

/**
 * Standard Levenshtein edit distance with substitution/insertion/deletion
 * each costing 1. Two-row dynamic programming so memory is O(min(a, b)).
 */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0
  if (a.length === 0) return b.length
  if (b.length === 0) return a.length

  // Make `a` the shorter string so we allocate the smaller row.
  if (a.length > b.length) {
    const tmp = a
    a = b
    b = tmp
  }

  const prev = new Array<number>(a.length + 1)
  const cur = new Array<number>(a.length + 1)
  for (let i = 0; i <= a.length; i++) prev[i] = i

  for (let j = 1; j <= b.length; j++) {
    cur[0] = j
    for (let i = 1; i <= a.length; i++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      cur[i] = Math.min(
        cur[i - 1] + 1, // insertion
        prev[i] + 1, // deletion
        prev[i - 1] + cost, // substitution
      )
    }
    for (let i = 0; i <= a.length; i++) prev[i] = cur[i]
  }
  return prev[a.length]
}

/** Length of the longest shared prefix between two strings. */
export function commonPrefixLength(a: string, b: string): number {
  const limit = Math.min(a.length, b.length)
  let i = 0
  while (i < limit && a[i] === b[i]) i++
  return i
}

/**
 * Should two normalized names be treated as the same store?
 *
 * - Identical strings → yes
 * - 70%+ overall similarity (1 − dist/maxLen) → yes
 * - Both are reasonably long (≥4 chars) AND share ≥70% of the shorter string
 *   as a common prefix → yes (handles chain stores: "ローソン" vs "ローソン100")
 */
export function shouldCluster(a: string, b: string): boolean {
  if (a === b) return true
  if (a.length === 0 || b.length === 0) return false

  // Quick reject when lengths differ wildly — Levenshtein cannot recover.
  const lenDiff = Math.abs(a.length - b.length)
  const minLen = Math.min(a.length, b.length)
  const maxLen = Math.max(a.length, b.length)
  if (lenDiff > minLen) {
    // Still allow strong-prefix override below
  } else {
    const dist = levenshtein(a, b)
    const sim = 1 - dist / maxLen
    if (sim >= 0.7) return true
  }

  // Strong shared-prefix path (chain root + branch variations)
  if (minLen >= 4) {
    const cp = commonPrefixLength(a, b)
    if (cp >= 4 && cp / minLen >= 0.7) return true
  }

  return false
}

/**
 * Cluster a list of store name variants and return a map from each input
 * to its canonical cluster name. Variants are passed in occurrence order
 * so we can pick the most-frequent one as canonical.
 */
export function clusterStoreNames(names: string[]): Map<string, string> {
  const result = new Map<string, string>()
  if (names.length === 0) return result

  // Deduplicate while preserving original frequency
  const freq = new Map<string, number>()
  for (const n of names) freq.set(n, (freq.get(n) ?? 0) + 1)
  const uniq = Array.from(freq.keys())
  const normalized = uniq.map(normalizeStoreName)

  // Union-find over unique names
  const parent = uniq.map((_, i) => i)
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]]
      i = parent[i]
    }
    return i
  }
  const union = (i: number, j: number) => {
    const ri = find(i)
    const rj = find(j)
    if (ri !== rj) parent[ri] = rj
  }

  for (let i = 0; i < uniq.length; i++) {
    if (normalized[i] === '') continue
    for (let j = i + 1; j < uniq.length; j++) {
      if (normalized[j] === '') continue
      if (shouldCluster(normalized[i], normalized[j])) union(i, j)
    }
  }

  // Pick canonical name per cluster: most frequent, shortest as tiebreak
  const groups = new Map<number, number[]>()
  for (let i = 0; i < uniq.length; i++) {
    const r = find(i)
    const arr = groups.get(r) ?? []
    arr.push(i)
    groups.set(r, arr)
  }
  for (const [, indices] of groups) {
    indices.sort((a, b) => {
      const fa = freq.get(uniq[a])!
      const fb = freq.get(uniq[b])!
      if (fa !== fb) return fb - fa
      return uniq[a].length - uniq[b].length
    })
    const canonical = uniq[indices[0]]
    for (const idx of indices) result.set(uniq[idx], canonical)
  }
  return result
}
